import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, ChevronLeft, ChevronRight as ChevronRightPag, Loader2, AlertCircle,
  CheckCircle2, RefreshCw, X, IndianRupee, Clock, TrendingUp, AlertTriangle, Search,
  Plus, Edit2, Trash2, Upload, Bell, Settings, MessageSquare, Send,
} from 'lucide-react';
import {
  getFeeSummary, getStudentFees, recordFeePayment,
  createInstallment, updateInstallment, deleteInstallment,
  getNotificationSettings, updateNotificationSettings,
  previewBulkSend, executeBulkSend, getNotificationHistory,
  FeeSummary, StudentFeeRow, Installment,
  NotificationSettings, NotificationHistoryItem,
} from '@/actions/data-fetch-fees';
import { apiRequest } from '@/utils/api-request';
import { getAcademicYears, AcademicYear } from '@/actions/data-fetch-academic-years';
import MultiClassSelect, { CurriculumGroup, ClassFilterChips } from '@/components/common/MultiClassSelect';
import { toastMessage } from '@/utils/toast-message';
import { erpPath } from '@/utils/erp-host';
import { hasPaPermission } from '@/utils/pa-permissions';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const NOTIF_TYPE_LABELS: Record<string, string> = {
  due_reminder:         'Due Reminder',
  due_today:            'Due Today',
  overdue_notice:       'Overdue Notice',
  payment_confirmation: 'Payment Confirmation',
  partial_payment:      'Partial Payment',
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_BADGE_MAP: Record<string, string> = {
  paid:    'b-success',
  partial: 'b-info',
  pending: 'b-neutral',
  overdue: 'b-danger',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`badge ${STATUS_BADGE_MAP[status] ?? STATUS_BADGE_MAP.pending}`}>
    <span className="dot"></span>
    {status}
  </span>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string; value: string | number; sub?: string;
  icon: React.ReactNode; color: string;
}> = ({ label, value, sub, icon, color }) => (
  <div className="stat" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
    <div className={`stat-icon ${color}`} style={{ background: `var(--${color.replace('cat-', 'c-')}-soft)`, color: `var(--${color.replace('cat-', 'c-')})`, flexShrink: 0, width: 34, height: 34, borderRadius: 8 }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div className="stat-label" style={{ marginBottom: 2, fontSize: 11 }}>{label}</div>
      <div className="stat-num" style={{ marginTop: 0, fontSize: 18 }}>{value}</div>
      {sub && <div className="meta" style={{ marginTop: 2, fontSize: 11 }}>{sub}</div>}
    </div>
  </div>
);

// ─── Toggle Row (for settings) ────────────────────────────────────────────────

const ToggleRow: React.FC<{
  label: string; desc: string; checked: boolean; onChange: () => void;
  bold?: boolean; extra?: React.ReactNode;
}> = ({ label, desc, checked, onChange, bold, extra }) => (
  <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border-2)' }}>
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: bold ? 700 : 600, fontSize: 13 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{desc}</div>
        {extra}
      </div>
      <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22, flexShrink: 0, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
        <span style={{
          position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
          background: checked ? 'var(--accent-deep)' : 'var(--border-2)',
          borderRadius: 11, transition: '0.2s',
        }}>
          <span style={{
            position: 'absolute', height: 16, width: 16, left: checked ? 21 : 3, bottom: 3,
            background: '#fff', borderRadius: '50%', transition: '0.2s',
          }} />
        </span>
      </label>
    </div>
  </div>
);

// ─── Notification Settings Modal ──────────────────────────────────────────────

const NotificationSettingsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNotificationSettings().then(res => {
      if (res.success && res.data) setSettings(res.data);
    }).finally(() => setLoading(false));
  }, []);

  const toggle = (key: keyof NotificationSettings) =>
    setSettings(s => s ? { ...s, [key]: !s[key] } : s);

  const setNum = (key: keyof NotificationSettings, val: number) =>
    setSettings(s => s ? { ...s, [key]: val } : s);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { id, school_id, ...body } = settings;
      const res = await updateNotificationSettings(body);
      if (res.success) { toastMessage('Settings saved', 'success'); onClose(); }
      else toastMessage('Failed to save settings', 'error');
    } catch { toastMessage('Something went wrong', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose}></div>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <div>
            <h2>Notification Settings</h2>
            <p>Control WhatsApp fee alerts for parents</p>
          </div>
          <button onClick={onClose} className="icon-btn" style={{ marginLeft: 'auto' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body col gap-0">
          {loading ? (
            <div className="center" style={{ padding: '40px 0' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} />
            </div>
          ) : settings ? (
            <>
              <div style={{ paddingBottom: 4, borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                <ToggleRow
                  label="WhatsApp Notifications"
                  desc="Master switch — enable all WhatsApp fee notifications for this school"
                  checked={!!settings.whatsapp_enabled}
                  onChange={() => toggle('whatsapp_enabled')}
                  bold />
              </div>

              <div style={{ opacity: settings.whatsapp_enabled ? 1 : 0.4, pointerEvents: settings.whatsapp_enabled ? 'auto' : 'none', transition: 'opacity 0.2s' }}>
                <ToggleRow
                  label="Due Reminder"
                  desc={`Send reminder ${settings.due_reminder_days} day(s) before due date`}
                  checked={!!settings.due_reminder_enabled}
                  onChange={() => toggle('due_reminder_enabled')}
                  extra={settings.due_reminder_enabled && (
                    <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Days before due:</span>
                      <input type="number" min="1" max="30" value={settings.due_reminder_days}
                        onChange={e => setNum('due_reminder_days', Number(e.target.value))}
                        className="input num" style={{ width: 64, height: 30, fontSize: 12 }} />
                    </div>
                  )} />
                <ToggleRow
                  label="Due Today"
                  desc="Alert parents on the day the fee is due"
                  checked={!!settings.due_today_enabled}
                  onChange={() => toggle('due_today_enabled')} />
                <ToggleRow
                  label="Overdue Notice"
                  desc={`Remind parents every ${settings.overdue_frequency_days} day(s) for unpaid overdue fees`}
                  checked={!!settings.overdue_enabled}
                  onChange={() => toggle('overdue_enabled')}
                  extra={settings.overdue_enabled && (
                    <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Repeat every:</span>
                      <input type="number" min="1" max="30" value={settings.overdue_frequency_days}
                        onChange={e => setNum('overdue_frequency_days', Number(e.target.value))}
                        className="input num" style={{ width: 64, height: 30, fontSize: 12 }} />
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>days</span>
                    </div>
                  )} />
                <ToggleRow
                  label="Payment Confirmation"
                  desc="Send confirmation when a full payment is recorded"
                  checked={!!settings.payment_confirmation_enabled}
                  onChange={() => toggle('payment_confirmation_enabled')} />
                <ToggleRow
                  label="Partial Payment Confirmation"
                  desc="Notify parent when a partial payment is recorded with the remaining balance"
                  checked={!!settings.partial_payment_enabled}
                  onChange={() => toggle('partial_payment_enabled')} />
              </div>
            </>
          ) : (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Failed to load settings.</div>
          )}
        </div>

        <div className="modal-foot">
          <button onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button onClick={handleSave} disabled={saving || loading || !settings} className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Send Reminders Modal ─────────────────────────────────────────────────────

const BULK_NOTIF_TYPES = [
  { value: 'due_reminder',   label: 'Due Reminder',   desc: 'Remind parents about upcoming fee due dates' },
  { value: 'due_today',      label: 'Due Today',       desc: 'Alert parents whose fee is due today' },
  { value: 'overdue_notice', label: 'Overdue Notice',  desc: 'Notify parents with overdue unpaid fees (grouped per student)' },
];

const SendRemindersModal: React.FC<{
  onClose: () => void;
  selectedYear?: number;
  selectedClasses: string[];
}> = ({ onClose, selectedYear, selectedClasses }) => {
  const [type, setType]       = useState('due_reminder');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ eligible: number; no_phone: number; already_sent: number } | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState<{ sent: number; failed: number; skipped_no_phone: number; skipped_dedup: number } | null>(null);

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await previewBulkSend({ type, academic_year_id: selectedYear, class_ids: selectedClasses });
      if (res.success && res.data) setPreview(res.data);
      else toastMessage('Failed to get preview', 'error');
    } catch { toastMessage('Something went wrong', 'error'); }
    finally { setPreviewing(false); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await executeBulkSend({ type, academic_year_id: selectedYear, class_ids: selectedClasses });
      if (res.success && res.data) {
        setResult(res.data);
        toastMessage(`Sent to ${res.data.sent} parent(s)`, 'success');
      } else {
        toastMessage('Failed to send notifications', 'error');
      }
    } catch { toastMessage('Something went wrong', 'error'); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose}></div>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <h2>Send Reminders</h2>
            <p>Send WhatsApp fee notifications to parents</p>
          </div>
          <button onClick={onClose} className="icon-btn" style={{ marginLeft: 'auto' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body col gap-16">
          {result ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <CheckCircle2 size={40} style={{ color: 'var(--success)', marginBottom: 12 }} />
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Notifications Sent!</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Sent', value: result.sent, color: 'var(--success)' },
                  { label: 'Failed', value: result.failed, color: 'var(--danger)' },
                  { label: 'No Phone', value: result.skipped_no_phone, color: 'var(--text)' },
                  { label: 'Already Sent', value: result.skipped_dedup, color: 'var(--text)' },
                ].map(s => (
                  <div key={s.label} style={{ padding: '14px 12px', background: 'var(--surface-2)', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="field">
                <label>Notification Type</label>
                <div className="col gap-8">
                  {BULK_NOTIF_TYPES.map(t => (
                    <label key={t.value} style={{
                      display: 'flex', gap: 10, padding: '10px 14px', cursor: 'pointer',
                      border: `1.5px solid ${type === t.value ? 'var(--accent-deep)' : 'var(--border-2)'}`,
                      borderRadius: 8,
                      background: type === t.value ? 'var(--accent-soft, #fef9e7)' : 'transparent',
                    }}>
                      <input type="radio" value={t.value} checked={type === t.value}
                        onChange={() => { setType(t.value); setPreview(null); }}
                        style={{ marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{t.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {preview && (
                <div style={{ padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Preview</div>
                  <div className="row" style={{ gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success)' }}>{preview.eligible}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Will receive</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>{preview.no_phone}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>No phone</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>{preview.already_sent}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Already sent</div>
                    </div>
                  </div>
                  {preview.eligible === 0 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-3)' }}>
                      No eligible recipients — either all are paid, no phone numbers on file, or reminders were recently sent.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button onClick={onClose} className="btn btn-ghost">{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            preview && preview.eligible > 0 ? (
              <button onClick={handleSend} disabled={sending} className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending…' : `Send to ${preview.eligible} parent${preview.eligible !== 1 ? 's' : ''}`}
              </button>
            ) : (
              <button onClick={handlePreview} disabled={previewing} className="btn btn-soft"
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell size={14} />}
                {previewing ? 'Checking…' : 'Preview'}
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
};

// ─── Record Payment Modal ─────────────────────────────────────────────────────

const METHODS = ['UPI', 'Cash', 'Cheque', 'NEFT', 'Bank Transfer', 'Other'];

const RecordPaymentModal: React.FC<{
  student: StudentFeeRow;
  preselectedInstallmentId?: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ student, preselectedInstallmentId, onClose, onSuccess }) => {
  const [installmentId, setInstallmentId] = useState<number | ''>(preselectedInstallmentId ?? '');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('UPI');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const unpaidInstallments = student.installments.filter(i => i.status !== 'paid');

  useEffect(() => {
    if (installmentId) {
      const inst = student.installments.find(i => i.id === installmentId);
      if (inst && !amount) setAmount(String(inst.balance));
    }
  }, [installmentId]);

  const handleSubmit = async () => {
    if (!installmentId) { toastMessage('Select an installment', 'error'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toastMessage('Enter a valid amount', 'error'); return; }
    if (!date) { toastMessage('Select a payment date', 'error'); return; }
    if (installmentId) {
      const inst = student.installments.find(i => i.id === installmentId);
      if (inst && amt > inst.balance + 0.005) {
        toastMessage(`Amount cannot exceed installment balance of ${fmt(inst.balance)}`, 'error');
        return;
      }
    }
    setSaving(true);
    try {
      const res = await recordFeePayment({
        student_id: student.student_id,
        fee_structure_id: installmentId || null,
        amount: amt,
        payment_date: date,
        payment_method: method,
        note: note.trim() || undefined,
      });
      if (res.success) {
        toastMessage('Payment recorded', 'success');
        onSuccess();
      } else {
        toastMessage(res.message || 'Failed to record payment', 'error');
      }
    } catch {
      toastMessage('Something went wrong', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose}></div>
      <div className="modal">
        <div className="modal-head">
          <div>
            <h2>Record Payment</h2>
            <p>{student.student_name} · {student.class_name}</p>
          </div>
          <button onClick={onClose} className="icon-btn" style={{ marginLeft: 'auto' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body col gap-16">
          <div className="field">
            <label>Installment</label>
            <select
              value={installmentId}
              onChange={e => setInstallmentId(e.target.value === '' ? '' : Number(e.target.value))}
              className="select">
              <option value="" disabled>Select installment</option>
              {unpaidInstallments.map(i => (
                <option key={i.id} value={i.id}>
                  {i.installment_name} — Balance {fmt(i.balance)} · Due {fmtDate(i.due_date)}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div className="field">
              <label>Amount (₹)</label>
              <input
                type="number" min="1" step="1"
                max={installmentId ? (student.installments.find(i => i.id === installmentId)?.balance ?? undefined) : undefined}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0"
                className="input num" />
            </div>
            <div className="field">
              <label>Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="input" />
            </div>
          </div>

          <div className="field">
            <label>Payment Method</label>
            <div className="row" style={{ flexWrap: 'wrap', gap: '8px' }}>
              {METHODS.map(m => (
                <button
                  key={m}
                  onClick={() => setMethod(m)}
                  className="chip"
                  style={{
                    height: '36px',
                    padding: '0 14px',
                    cursor: 'pointer',
                    background: method === m ? 'var(--slate)' : 'var(--surface)',
                    color: method === m ? '#fff' : 'var(--text-2)',
                    border: method === m ? 'none' : '1.5px solid var(--border-2)',
                  }}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Note <span className="opt">(optional)</span></label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Cheque #1234"
              className="input" />
          </div>
        </div>

        <div className="modal-foot">
          <button onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Record Payment'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Student Fee Detail Modal ─────────────────────────────────────────────────

const StudentFeeDetailModal: React.FC<{
  student: StudentFeeRow;
  onClose: () => void;
  onChanged: () => void;
  years: AcademicYear[];
  selectedYear?: number;
}> = ({ student, onClose, onChanged, selectedYear }) => {
  const [activeTab, setActiveTab] = useState<'installments' | 'notifications'>('installments');
  const [notifHistory, setNotifHistory] = useState<NotificationHistoryItem[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const [installments, setInstallments] = useState<Installment[]>(student.installments);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ installment_name: string; installment_amount: string; due_date: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ installment_name: '', installment_amount: '', due_date: '', academic_year_id: String(selectedYear ?? '') });
  const [saving, setSaving] = useState(false);

  const [showPayment, setShowPayment] = useState(false);
  const [payInstId, setPayInstId] = useState<number | ''>('');
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payMethod, setPayMethod] = useState('UPI');
  const [payNote, setPayNote] = useState('');
  const [paying, setPaying] = useState(false);

  const instSum     = installments.reduce((s, i) => s + i.amount, 0);
  const totalBilled = student.total_billed > 0 ? student.total_billed : instSum;
  const totalPaid   = installments.reduce((s, i) => s + i.paid_amount, 0);
  const balance     = totalBilled - totalPaid;

  const loadNotifHistory = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await getNotificationHistory({ student_id: student.student_id, limit: 50 });
      if (res.success && res.data) setNotifHistory(res.data.data);
    } catch {} finally { setNotifLoading(false); }
  }, [student.student_id]);

  const startEdit = (inst: Installment) => {
    setEditingId(inst.id);
    setDraft({ installment_name: inst.installment_name, installment_amount: String(inst.amount), due_date: inst.due_date?.split('T')[0] ?? '' });
    setAdding(false);
  };
  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const billedCap = student.total_billed;

  const saveEdit = async () => {
    if (!editingId || !draft) return;
    if (!draft.installment_name.trim()) { toastMessage('Installment name is required', 'error'); return; }
    if (!draft.due_date) { toastMessage('Due date is required', 'error'); return; }
    if (!draft.installment_amount || parseFloat(draft.installment_amount) <= 0) { toastMessage('Enter a valid installment amount', 'error'); return; }
    const currentInst = installments.find(i => i.id === editingId);
    const newAmt = parseFloat(draft.installment_amount);
    if (billedCap > 0 && currentInst) {
      const otherSum = installments.filter(i => i.id !== editingId).reduce((s, i) => s + i.amount, 0);
      if (otherSum + newAmt > billedCap) {
        toastMessage(`Only ${fmt(billedCap - otherSum)} remaining to allocate`, 'error');
        return;
      }
    }
    setSaving(true);
    try {
      const res = await updateInstallment(editingId, {
        installment_name: draft.installment_name,
        installment_amount: parseFloat(draft.installment_amount),
        due_date: draft.due_date,
      });
      if (res.success) {
        setInstallments(prev => prev.map(i => i.id === editingId
          ? { ...i, installment_name: draft.installment_name, amount: parseFloat(draft.installment_amount), due_date: draft.due_date }
          : i));
        cancelEdit();
        toastMessage('Installment updated', 'success');
        onChanged();
      }
    } catch { toastMessage('Failed to update', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      const res = await deleteInstallment(id);
      if (res.success) {
        setInstallments(prev => prev.filter(i => i.id !== id));
        toastMessage('Installment deleted', 'success');
        onChanged();
      }
    } catch { toastMessage('Failed to delete', 'error'); }
    finally { setSaving(false); }
  };

  const startAdd = () => {
    if (!selectedYear) {
      toastMessage('Please select an academic year from the top filter first', 'warning');
      return;
    }
    setAdding(true);
    setNewRow({ installment_name: '', installment_amount: '', due_date: '', academic_year_id: String(selectedYear) });
    setEditingId(null);
    setDraft(null);
  };

  const saveNew = async () => {
    if (!newRow.installment_name.trim()) { toastMessage('Installment name is required', 'error'); return; }
    if (!newRow.due_date) { toastMessage('Due date is required', 'error'); return; }
    if (!newRow.installment_amount || parseFloat(newRow.installment_amount) <= 0) { toastMessage('Enter a valid installment amount', 'error'); return; }
    const newAmt = parseFloat(newRow.installment_amount);
    if (billedCap > 0 && instSum + newAmt > billedCap) {
      toastMessage(`Only ${fmt(billedCap - instSum)} remaining to allocate`, 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await createInstallment({
        student_id: student.student_id,
        installment_name: newRow.installment_name,
        installment_amount: parseFloat(newRow.installment_amount),
        due_date: newRow.due_date,
        academic_year_id: selectedYear!,
      });
      if (!res.success) {
        toastMessage((res as any).message ?? 'Failed to add installment', 'error');
        return;
      }
      if (res.success) {
        const due = new Date(newRow.due_date);
        const optimistic: Installment = {
          id: res.data?.id ?? Date.now(),
          installment_name: newRow.installment_name,
          amount: parseFloat(newRow.installment_amount),
          due_date: newRow.due_date,
          paid_amount: 0,
          balance: parseFloat(newRow.installment_amount),
          status: due < new Date() ? 'overdue' : 'pending',
          academic_year: null,
          academic_year_id: newRow.academic_year_id ? Number(newRow.academic_year_id) : 0,
          payments: [],
        };
        setInstallments(prev => [...prev, optimistic]);
        setAdding(false);
        toastMessage('Installment added', 'success');
        onChanged();
      }
    } catch { toastMessage('Failed to add', 'error'); }
    finally { setSaving(false); }
  };

  const openPayment = (installmentId?: number) => {
    const inst = installmentId ? installments.find(i => i.id === installmentId) : undefined;
    setPayInstId(installmentId ?? '');
    setPayAmount(inst ? String(inst.balance) : '');
    setPayDate(new Date().toISOString().split('T')[0]);
    setPayMethod('UPI');
    setPayNote('');
    setShowPayment(true);
  };

  const handlePayment = async () => {
    if (!payInstId) { toastMessage('Select an installment', 'error'); return; }
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toastMessage('Enter a valid amount', 'error'); return; }
    if (!payDate) { toastMessage('Select a payment date', 'error'); return; }
    if (payInstId) {
      const inst = installments.find(i => i.id === payInstId);
      if (inst && amt > inst.balance + 0.005) {
        toastMessage(`Amount cannot exceed installment balance of ${fmt(inst.balance)}`, 'error');
        return;
      }
    }
    setPaying(true);
    try {
      const res = await recordFeePayment({
        student_id: student.student_id,
        fee_structure_id: payInstId || null,
        amount: amt,
        payment_date: payDate,
        payment_method: payMethod,
        note: payNote.trim() || undefined,
      });
      if (!res.success) { toastMessage((res as any).message || 'Failed to record payment', 'error'); return; }
      toastMessage('Payment recorded', 'success');
      if (payInstId) {
        setInstallments(prev => prev.map(i => {
          if (i.id === payInstId) {
            const newPaid  = Math.round((i.paid_amount + amt) * 100) / 100;
            const newBal   = Math.round((i.amount - newPaid) * 100) / 100;
            const newStatus = newBal <= 0.005 ? 'paid' : newPaid > 0 ? 'partial' : i.status;
            return { ...i, paid_amount: newPaid, balance: newBal, status: newStatus };
          }
          return i;
        }));
      }
      setShowPayment(false);
      onChanged();
    } catch { toastMessage('Something went wrong', 'error'); }
    finally { setPaying(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose}></div>
      <div className="modal" style={{ maxWidth: 900, width: '90vw' }}>
        <div className="modal-head">
          <div>
            <h2>{student.student_name}</h2>
            <p>{student.class_name}</p>
          </div>
          <button onClick={onClose} className="icon-btn" style={{ marginLeft: 'auto' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Summary row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          {[
            { label: 'Total Billed', value: fmt(totalBilled), color: 'var(--text)' },
            { label: 'Paid',         value: fmt(totalPaid),   color: 'var(--success)' },
            { label: 'Balance',      value: fmt(balance),     color: balance > 0 ? 'var(--danger)' : 'var(--text)' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px', gap: 0 }}>
          {(['installments', 'notifications'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'notifications' && !notifHistory.length && !notifLoading) loadNotifHistory();
              }}
              style={{
                padding: '10px 16px', fontSize: 13,
                fontWeight: activeTab === tab ? 700 : 500,
                color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab ? '2px solid var(--accent-deep)' : '2px solid transparent',
                marginBottom: -1,
              }}>
              {tab === 'installments' ? 'Installments' : 'Notifications'}
            </button>
          ))}
        </div>

        {/* Notifications tab */}
        {activeTab === 'notifications' ? (
          <div className="modal-body" style={{ padding: 0, overflowY: 'auto', maxHeight: '45vh' }}>
            {notifLoading ? (
              <div className="center" style={{ padding: '40px 0' }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--accent-deep)' }} />
              </div>
            ) : notifHistory.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <MessageSquare size={32} style={{ color: 'var(--text-3)', marginBottom: 10 }} />
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>No notifications sent yet</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6 }}>
                  WhatsApp reminders and payment confirmations will appear here once sent.
                </div>
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Phone</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {notifHistory.map(n => (
                    <tr key={n.id}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>
                        {NOTIF_TYPE_LABELS[n.notification_type] ?? n.notification_type}
                      </td>
                      <td className="meta">{n.sent_at ? fmtDate(n.sent_at) : fmtDate(n.created_at)}</td>
                      <td className="meta">{n.phone}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${n.status === 'sent' ? 'b-success' : n.status === 'failed' ? 'b-danger' : 'b-neutral'}`}>
                          <span className="dot"></span>{n.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* Installments tab */
          <div className="modal-body" style={{ padding: 0, overflowY: 'auto', maxHeight: '45vh' }}>
            {showPayment ? (
              <div className="col gap-16" style={{ padding: '20px 24px' }}>
                <div className="field">
                  <label>Installment</label>
                  <select
                    value={payInstId}
                    onChange={e => {
                      const id = e.target.value === '' ? '' : Number(e.target.value);
                      setPayInstId(id);
                      const inst = id ? installments.find(i => i.id === id) : undefined;
                      if (inst) setPayAmount(String(inst.balance));
                    }}
                    className="select">
                    <option value="" disabled>Select installment</option>
                    {installments.filter(i => i.status !== 'paid').map(i => (
                      <option key={i.id} value={i.id}>{i.installment_name} — Balance {fmt(i.balance)} · Due {fmtDate(i.due_date)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="field">
                    <label>Amount (₹)</label>
                    <input
                      type="number" min="1" step="1"
                      max={payInstId ? (installments.find(i => i.id === payInstId)?.balance ?? undefined) : undefined}
                      value={payAmount}
                      onChange={e => setPayAmount(e.target.value)}
                      placeholder="0"
                      className="input num" />
                  </div>
                  <div className="field">
                    <label>Date</label>
                    <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} className="input" />
                  </div>
                </div>
                <div className="field">
                  <label>Payment Method</label>
                  <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                    {METHODS.map(m => (
                      <button key={m} onClick={() => setPayMethod(m)} className="chip" style={{ height: 36, padding: '0 14px', cursor: 'pointer', background: payMethod === m ? 'var(--slate)' : 'var(--surface)', color: payMethod === m ? '#fff' : 'var(--text-2)', border: payMethod === m ? 'none' : '1.5px solid var(--border-2)' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <label>Note <span className="opt">(optional)</span></label>
                  <input type="text" value={payNote} onChange={e => setPayNote(e.target.value)} placeholder="e.g. Cheque #1234" className="input" />
                </div>
              </div>
            ) : installments.length === 0 && !adding ? (
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>No installments added yet</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6 }}>
                  Add at least one installment before recording a payment.<br />
                  For a full upfront payment, add a single installment with the total fee amount.
                </div>
              </div>
            ) : (
              <table className="tbl tbl-sticky">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>Installment</th>
                    <th>Due Date</th>
                    <th style={{ textAlign: 'right' }}>Billed</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ width: '110px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map(inst => editingId === inst.id && draft ? (
                    <tr key={inst.id} style={{ background: 'var(--surface-2)' }}>
                      <td>
                        <input autoFocus value={draft.installment_name}
                          onChange={e => setDraft(d => d ? { ...d, installment_name: e.target.value } : d)}
                          className="input" style={{ fontSize: 12, height: 32, width: '100%' }} placeholder="Name" />
                      </td>
                      <td>
                        <input type="date" value={draft.due_date}
                          onChange={e => setDraft(d => d ? { ...d, due_date: e.target.value } : d)}
                          className="input" style={{ fontSize: 12, height: 32, width: '100%' }} />
                      </td>
                      <td>
                        <input type="number" min="1" value={draft.installment_amount}
                          onChange={e => setDraft(d => d ? { ...d, installment_amount: e.target.value } : d)}
                          className="input num" style={{ fontSize: 12, height: 32, width: '100%', textAlign: 'right' }} placeholder="0" />
                      </td>
                      <td colSpan={3} style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>paid, balance & status are auto-calculated</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={saveEdit} disabled={saving} className="icon-btn" style={{ width: 30, height: 30, color: 'var(--success)' }}>
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                          </button>
                          <button onClick={cancelEdit} className="icon-btn" style={{ width: 30, height: 30 }}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={inst.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{inst.installment_name}</div>
                        {inst.payments[0]?.payment_note && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{inst.payments[0].payment_note}</div>
                        )}
                      </td>
                      <td className="meta">{fmtDate(inst.due_date)}</td>
                      <td className="num" style={{ textAlign: 'right' }}>{fmt(inst.amount)}</td>
                      <td className="num" style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>{fmt(inst.paid_amount)}</td>
                      <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(inst.balance)}</td>
                      <td style={{ textAlign: 'center' }}><StatusBadge status={inst.status} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {inst.status !== 'paid' && (
                            <button onClick={() => openPayment(inst.id)} className="btn btn-sm btn-soft" style={{ padding: '0 8px', height: 28, fontSize: 11 }}>
                              Record
                            </button>
                          )}
                          {inst.status !== 'paid' && (
                            <button onClick={() => startEdit(inst)} className="icon-btn" style={{ width: 28, height: 28 }} title="Edit">
                              <Edit2 size={12} />
                            </button>
                          )}
                          {inst.status !== 'paid' && (
                            <button onClick={() => handleDelete(inst.id)} disabled={saving} className="icon-btn" style={{ width: 28, height: 28, color: 'var(--danger)' }} title="Delete">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}

                  {adding && (
                    <tr style={{ background: 'var(--surface-2)' }}>
                      <td>
                        <input autoFocus value={newRow.installment_name}
                          onChange={e => setNewRow(r => ({ ...r, installment_name: e.target.value }))}
                          className="input" style={{ fontSize: 12, height: 32, width: '100%' }} placeholder="e.g. Term 1 Fee" />
                      </td>
                      <td>
                        <input type="date" value={newRow.due_date}
                          onChange={e => setNewRow(r => ({ ...r, due_date: e.target.value }))}
                          className="input" style={{ fontSize: 12, height: 32, width: '100%' }} />
                      </td>
                      <td>
                        <input type="number" min="0" max={billedCap > 0 ? billedCap - instSum : undefined} value={newRow.installment_amount}
                          onChange={e => setNewRow(r => ({ ...r, installment_amount: e.target.value }))}
                          className="input num" style={{ fontSize: 12, height: 32, width: '100%', textAlign: 'right' }} placeholder="0" />
                      </td>
                      <td colSpan={3} style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>paid, balance & status are auto-calculated</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={saveNew} disabled={saving} className="icon-btn" style={{ width: 30, height: 30, color: 'var(--success)' }}>
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                          </button>
                          <button onClick={() => setAdding(false)} className="icon-btn" style={{ width: 30, height: 30 }}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="modal-foot">
          {activeTab === 'notifications' ? (
            <button onClick={onClose} className="btn btn-ghost">Close</button>
          ) : showPayment ? (
            <>
              <button onClick={() => setShowPayment(false)} disabled={paying} className="btn btn-ghost">Back</button>
              <button onClick={handlePayment} disabled={paying} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {paying ? 'Saving…' : 'Record Payment'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="btn btn-ghost">Close</button>
              <button onClick={startAdd} disabled={adding || (billedCap > 0 && instSum >= billedCap)} className="btn btn-soft" style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={billedCap > 0 && instSum >= billedCap ? 'Total billed amount fully allocated' : undefined}>
                <Plus size={14} /> Add Installment
              </button>
              {balance > 0 && (
                <button onClick={() => openPayment()} className="btn btn-primary">
                  + Record Payment
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Status filter tabs ───────────────────────────────────────────────────────

const STATUS_TABS = ['all', 'paid', 'partial', 'pending', 'overdue'] as const;

// ─── Main Component ───────────────────────────────────────────────────────────

const PAGE_LIMIT = 20;

const FeeManagement: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary]           = useState<FeeSummary | null>(null);
  const [students, setStudents]         = useState<StudentFeeRow[]>([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [years, setYears]               = useState<AcademicYear[]>([]);
  const [classGroups, setClassGroups]   = useState<CurriculumGroup[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch]             = useState('');
  const [searchInput, setSearchInput]   = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [detailStudent, setDetailStudent]   = useState<StudentFeeRow | null>(null);
  const [paymentModal, setPaymentModal]     = useState<{ student: StudentFeeRow; installmentId?: number } | null>(null);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal]   = useState(false);

  const fetchData = useCallback(async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, studentsRes] = await Promise.all([
        getFeeSummary(selectedYear, selectedClasses),
        getStudentFees({ academic_year_id: selectedYear, class_ids: selectedClasses, status: statusFilter, search: search || undefined, page: p, limit: PAGE_LIMIT }),
      ]);
      if (summaryRes.success && summaryRes.data) setSummary(summaryRes.data);
      if (studentsRes.success && studentsRes.data) {
        setStudents(studentsRes.data.data);
        setTotal(studentsRes.data.total);
      }
    } catch {
      setError('Failed to load fee data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedClasses, statusFilter, search]);

  useEffect(() => {
    getAcademicYears().then(({ years }) => setYears(years));
    apiRequest<any>('/api/parent-app/admin/classes').then(res => {
      setClassGroups(res?.data?.grouped ?? []);
    });
  }, []);

  useEffect(() => {
    if (!selectedYear) { setStudents([]); setTotal(0); setSummary({ total_billed: 0, total_paid: 0, total_pending: 0, overdue_count: 0 }); setLoading(false); return; }
    setPage(1); fetchData(1);
  }, [selectedYear, selectedClasses, statusFilter, search]);

  const openPaymentModal = (student: StudentFeeRow, installmentId?: number) =>
    setPaymentModal({ student, installmentId });
  const closePaymentModal = () => setPaymentModal(null);
  const handlePaymentSuccess = () => { closePaymentModal(); fetchData(page); };

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 400);
  };

  const totalPages = Math.ceil(total / PAGE_LIMIT);
  const displayed = students;

  return (
    <>
      {/* Header */}
      <div className="page-head" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="page-head-icon">
            <DollarSign className="w-6 h-6" />
          </div>
          <div className="page-head-text">
            <h1>Fee Management</h1>
            <p>Track installments and record payments</p>
          </div>
        </div>
        {hasPaPermission('fees', 'import') && (
          <button
            onClick={() => navigate(erpPath('bulk-import?module=fees'))}
            className="btn btn-secondary"
          >
            <Upload size={17} />
            Import
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setShowRemindersModal(true)}
            className="btn btn-soft"
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
            <Bell size={15} /> Send Reminders
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="icon-btn"
            title="Notification Settings">
            <Settings size={16} />
          </button>
        </div>
      </div>

      <div className="page-body col gap-20" style={{ overflow: 'hidden', paddingBottom: 20 }}>
        {/* Summary Cards */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <StatCard
              label="Total Billed"
              value={fmt(summary.total_billed)}
              icon={<IndianRupee className="w-5 h-5" />}
              color="cat-blue" />
            <StatCard
              label="Collected"
              value={fmt(summary.total_paid)}
              sub={summary.total_billed > 0 ? `${Math.round((summary.total_paid / summary.total_billed) * 100)}% collected` : undefined}
              icon={<TrendingUp className="w-5 h-5" />}
              color="cat-green" />
            <StatCard
              label="Pending"
              value={fmt(summary.total_pending)}
              icon={<Clock className="w-5 h-5" />}
              color="cat-amber" />
            <StatCard
              label="Overdue Installments"
              value={summary.overdue_count}
              icon={<AlertTriangle className="w-5 h-5" />}
              color="cat-rose" />
          </div>
        )}

        {/* Filters */}
        <div className="row" style={{ gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', width: '280px' }}>
            <Search className="w-4 h-4" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search student…"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              className="input"
              style={{ paddingLeft: '36px' }} />
          </div>

          <select
            value={selectedYear ?? ''}
            onChange={e => setSelectedYear(e.target.value ? Number(e.target.value) : undefined)}
            className="select"
            style={{ width: '180px' }}>
            <option value="">Select Year</option>
            {years.map(y => <option key={y.id} value={y.id}>{y.year_label}</option>)}
          </select>

          <MultiClassSelect
            classGroups={classGroups}
            selectedIds={selectedClasses}
            onChange={ids => { setSelectedClasses(ids); }}
            disabled={!selectedYear}
          />

          {(searchInput || selectedYear || selectedClasses.length > 0 || statusFilter !== 'all') && (
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="icon-btn"
              title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}

          <div className="seg" style={{ marginLeft: 'auto' }}>
            {STATUS_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={statusFilter === tab ? 'active' : ''}>
                {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Class filter chips */}
        <ClassFilterChips
          classGroups={classGroups}
          selectedIds={selectedClasses}
          onChange={setSelectedClasses}
        />

        {/* Error */}
        {error && (
          <div className="row gap-12" style={{ padding: '14px 16px', background: 'var(--danger-wash)', border: '1px solid var(--danger-soft)', borderRadius: 'var(--r-card)', color: 'var(--danger)' }}>
            <AlertCircle className="w-4 h-4" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span className="meta" style={{ color: 'inherit' }}>{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="card" style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div className="center" style={{ padding: '64px 20px' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} />
            </div>
          ) : !selectedYear ? (
            <div className="empty">
              <div className="empty-art">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <h3>Select an Academic Year</h3>
              <p>Choose an academic year from the filter above to view and manage fee records.</p>
            </div>
          ) : displayed.length === 0 ? (
            <div style={{ padding: '64px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13.5px' }}>
              {search ? 'No students match your search.' : 'No fee records found.'}
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <table className="tbl tbl-sticky">
                <thead>
                  <tr>
                    <th style={{ width: '24%' }}>Student</th>
                    <th style={{ textAlign: 'right' }}>Billed</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map(student => (
                    <tr
                      key={student.student_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setDetailStudent(student)}>
                      <td>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{student.student_name}</div>
                        <div className="meta">{student.class_name}</div>
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>{fmt(student.total_billed)}</td>
                      <td className="num" style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>{fmt(student.total_paid)}</td>
                      <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(student.balance)}</td>
                      <td style={{ textAlign: 'center' }}><StatusBadge status={student.status} /></td>
                      <td style={{ textAlign: 'center' }}>
                        {student.status !== 'paid' && (
                          <button
                            onClick={e => { e.stopPropagation(); openPaymentModal(student); }}
                            className="btn btn-sm btn-soft">
                            Record
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && totalPages > 1 && (
          <div className="row" style={{ justifyContent: 'center', gap: '16px', flexShrink: 0 }}>
            <span className="meta">
              Showing {(page - 1) * PAGE_LIMIT + 1}–{Math.min(page * PAGE_LIMIT, total)} of {total} students
            </span>
            <div className="row gap-8">
              <button
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchData(p); }}
                disabled={page === 1}
                className="icon-btn"
                style={{ opacity: page === 1 ? 0.5 : 1 }}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="meta">{page} / {totalPages}</span>
              <button
                onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchData(p); }}
                disabled={page >= totalPages}
                className="icon-btn"
                style={{ opacity: page >= totalPages ? 0.5 : 1 }}>
                <ChevronRightPag className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {detailStudent && (
        <StudentFeeDetailModal
          student={detailStudent}
          onClose={() => setDetailStudent(null)}
          onChanged={() => fetchData(page)}
          years={years}
          selectedYear={selectedYear} />
      )}

      {paymentModal && (
        <RecordPaymentModal
          student={paymentModal.student}
          preselectedInstallmentId={paymentModal.installmentId}
          onClose={closePaymentModal}
          onSuccess={handlePaymentSuccess} />
      )}

      {showRemindersModal && (
        <SendRemindersModal
          onClose={() => setShowRemindersModal(false)}
          selectedYear={selectedYear}
          selectedClasses={selectedClasses} />
      )}

      {showSettingsModal && (
        <NotificationSettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
    </>
  );
};

export default FeeManagement;
